import { AppBar, makeStyles, styled } from "@material-ui/core";
import { Linked, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, Page, SelectSortBy, span, TilesList, Toolbar } from "lqx-react";
import * as React from "react";
import { useMemo } from "react";
import { RowH1 } from "../../../../../common";
import { RemittanceAdviceVaultCard } from "../api/RemittanceAdviceVaultCart";
import { RemittanceAdviceVaultPage } from "../api/remittanceAdviceVaultPage";
import { AddFilter } from "../components/search-bar/search-bar";
import { CountListSection } from "../count-list-section";
import { SortOptions } from "./models/remittance-advice-fillter";
import { RemittanceAdviceStore } from "./models/remittance-advice-store";
import { RemittanceAdviceVaultCardRenderer } from './remittance-advice-card-renderer';
import { RemittanceAdviceDetailsPane } from "./remittance-advice-details-pane";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can } from "src/app/services/can";

export const RemittanceAdvicePage = () => {
    const state = useModel(RemittanceAdviceStore);
    const [isReady, setIsReady] = React.useState(false);
    const $activeRemittanceNoteCard: Linked<RemittanceAdviceVaultCard> = useLinked<RemittanceAdviceVaultCard>(null);
    const remittanceAdviceVaultPage = useModel(RemittanceAdviceVaultPage);
    const $selectedRemittanceNoteDetailsTab = useLinked("details");
    const refList = React.useRef(null)
    const classes = useStyles();
    const [remittanceAdvicesVaultCard, setRemittanceAdvicesVaultCard] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    let [countAll, setCountAll] = React.useState(0);

    useIO(async () => {
        await Promise.all([
            state.payors.fetch(),
            state.payees.fetch()
        ])

        state.initialize()
        await fetchRemittanceAdvice();
        setCountAll(remittanceAdviceVaultPage.remittanceNotes.count);
        setRemittanceAdvicesVaultCard(remittanceAdviceVaultPage.remittanceNotes.items.models)
    }, []);

    const fetchRemittanceAdvice = async (skip = 0, count = 100) => {
        const params = state.filter.mapToQuery(skip, count);
        setIsLoading(true)
        await remittanceAdviceVaultPage.remittanceNotes.fetch({ params: params }).catch(e => console.error(e));
        setIsLoading(false);
        setIsReady(true);
    }

    const closeDetailsPane = () => $activeRemittanceNoteCard.set(null);
    const onVaultCardClicked = () => $selectedRemittanceNoteDetailsTab.set('details');

    useMemo(async () => {
        if (isReady) {
            setIsReady(false);
            $activeRemittanceNoteCard.set(null);
            await fetchRemittanceAdvice();
            setRemittanceAdvicesVaultCard(remittanceAdviceVaultPage.remittanceNotes.items.models);
        }
    }, [state.filter['_changeToken']]);

    return <>
        <Page maxWidth={false} header={
            <Flex center>
                <RowH1 className={classes.title}>Remittance Advice</RowH1>
            </Flex>
        }
            top={
                <AppBar position="static" color="secondary">
                    <Toolbar>
                        <StyledFilterBar filter={state.filter} filterAllPlaceholder="Filter by Payor Name, Payee Name, Payment Amount, Issuance Date, Currency, Import Job ID, Invoice Number or Face Value" />
                        {/* <StateFilter stateValue={state.filter.$.stateId} filterOptions={RemittanceAdviceStateOptions} /> */}
                        <SelectSortBy $attr={state.filter.sortBy.$.attr} $asc={state.filter.sortBy.$.asc} options={SortOptions} />
                    </Toolbar>
                </AppBar>
            }>
            {
                isReady ?
                    <Columns style={{ minHeight: 'calc(100% - 80px)' }}>
                        <TilesListColumn>
                            <CountListSection countAll={countAll} countFiltered={remittanceAdviceVaultPage.remittanceNotes.count}></CountListSection>
                            <TilesList
                                items={remittanceAdvicesVaultCard}
                                itemSize={100}
                                renderer={RemittanceAdviceVaultCardRenderer($activeRemittanceNoteCard, (vaultCard: RemittanceAdviceVaultCard) => {
                                    return (
                                        <>
                                            {
                                                !$activeRemittanceNoteCard.value && Can(UserEntitlementCatalogs.EditVaultRemittanceAdvicesPage) && <EditButton onClick={evt => {
                                                    $selectedRemittanceNoteDetailsTab.set("edit");
                                                    $activeRemittanceNoteCard.set(vaultCard);
                                                    evt.stopPropagation();
                                                }} color="primary">Edit</EditButton>
                                            }
                                            {
                                                vaultCard.dataset.isDigitized &&
                                                <SendToButton color="primary"
                                                    onClick={
                                                        evt => {
                                                            $selectedRemittanceNoteDetailsTab.set("sendTo");
                                                            $activeRemittanceNoteCard.set(vaultCard);
                                                            evt.stopPropagation();
                                                        }
                                                    }>Send To</SendToButton>
                                            }
                                        </>
                                    )
                                }, onVaultCardClicked)}
                                selected={undefined}
                            />
                        </TilesListColumn>
                        {$activeRemittanceNoteCard.value &&
                            <RemittanceDetailsPaneColumn>
                                <RemittanceAdviceDetailsPane close={closeDetailsPane} remittanceNote={$activeRemittanceNoteCard.value.dataset} remittanceNoteId={$activeRemittanceNoteCard.value.id} remittanceAdviceData={$activeRemittanceNoteCard.value} $selectedTab={$selectedRemittanceNoteDetailsTab} />
                            </RemittanceDetailsPaneColumn>}
                    </Columns> : <Loading />
            }
        </Page>
    </>
}

const TilesListColumn = styled(Column)({
    minWidth: '500px',
    minHeight: '250px'
});

const RemittanceDetailsPaneColumn = styled(Column)({
    minWidth: '250px'
});

const useStyles = makeStyles({
    title: {
        padding: '20px 0',
        color: '#3d6889',
        fontSize: '24px'
    }
});

const StyledFilterBar = styled(AddFilter)({
    width: '650px',
    alignItems: 'center',
    padding: '4px',
});

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

const SendToButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});